<script setup lang="ts">
import { computed, ref, toRaw, watch } from 'vue';
import Dialog from '@/components/Dialog/Dialog.vue';
import Button from '@/components/buttons/Button.vue';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import Autocomplete from '@/components/forms/Autocomplete/Autocomplete.vue';
import Checkbox from '@/components/forms/Checkbox/Checkbox.vue';
import Select from '@/components/forms/Select/Select.vue';
import TextField from '@/components/forms/TextField/TextField.vue';
import FormAddButton from './FormAddButton.vue';
import PtwDeleteButton from '@/components/buttons/PtwDeleteButton.vue';
import Notification from '@/components/notifications/Notification.vue';
import { NotificationMessage } from '@/components/notifications/types';
import { mdiUndo } from '@mdi/js';
import { I18nKey } from '@/utils/typings';
import { truthy } from '@/utils/helper';
import { useFormMode } from '@/components/forms/useFormMode.ts';
import { useTranslation } from 'i18next-vue';
import { uniq } from 'lodash';
import {
  instanceOfTrackItMeasurementPropertyFilterDto,
  TrackItMeasurementFilterForCombinationDto,
  TrackItMeasurementParameterFilterDto,
  TrackItMeasurementPropertyFilterDto,
} from '@/api/generator';
import {
  createMeasurementParameter,
  createParameterFilterEntry,
  createPropertyFilterEntry,
  filterLabelParts,
  isParameterFilterEntry,
  kindOfMeasurementParameter,
  measurementParameterKindAllowsUnit,
  trackItFilterIdentityKey,
  trackItFilterName,
  trackItMeasurementParameterKinds,
  TrackItMeasurementParameterKind,
  trackItParameterIdentityKey,
  trackItPropertyIdentityKey,
  translateMeasurementPropertyName,
  unitOfMeasurementParameter,
} from './trackItFilters';
import TrackItUnitCombobox from './TrackItUnitCombobox.vue';
import {
  useTrackItFilterValidation,
  type TrackItFilterValidationEntry,
} from './useTrackItFilterValidation';

const mode = useFormMode();
const { t } = useTranslation();

const props = defineProps<{
  modelValue: boolean;
  filtersFromCombinations: TrackItMeasurementParameterFilterDto[];
  propertiesFromCombinations: TrackItMeasurementPropertyFilterDto[];
  trackItFilterCatalog: TrackItMeasurementFilterForCombinationDto[];
  valueBearingCounts: Map<string, number>;
  valueBearingPropertyCounts: Map<string, number>;
}>();

const emit = defineEmits<{
  'update:modelValue': [open: boolean];
  apply: [
    selectedFilters: TrackItMeasurementParameterFilterDto[],
    selectedProperties: TrackItMeasurementPropertyFilterDto[],
  ];
  // Carries the staged parameter filters so the backend's suggestion request can diff against
  // what the user has picked in this dialog session, not just what's already persisted.
  requestParameterSuggestions: [stagedFilters: TrackItMeasurementParameterFilterDto[]];
}>();

type AdditionalRow = {
  name: string;
  kind: TrackItMeasurementParameterKind;
  unit?: string;
};

const parameterEntries = computed(() => props.trackItFilterCatalog.filter(isParameterFilterEntry));
const propertyEntries = computed(() =>
  props.trackItFilterCatalog.filter(instanceOfTrackItMeasurementPropertyFilterDto),
);

// Keyed by identity, not name: two catalog entries can share a name and differ in kind.
const parameterEntryByIdentityKey = computed(
  () => new Map(parameterEntries.value.map((entry) => [trackItFilterIdentityKey(entry), entry])),
);

const propertyEntryByName = computed(
  () => new Map(propertyEntries.value.map((entry) => [trackItFilterName(entry) ?? '', entry])),
);

function additionalRowFromFilter(filter: TrackItMeasurementParameterFilterDto): AdditionalRow {
  const param = filter.measurementParameter;
  return {
    name: trackItFilterName(filter) ?? '',
    kind: param ? kindOfMeasurementParameter(param) : 'String',
    unit: param ? unitOfMeasurementParameter(param) : undefined,
  };
}

// The Autocomplete slots type their item without `raw`, the original item with label and identifier.
const itemParts = (item: object) => {
  const { label, identifier } = (item as { raw: ReturnType<typeof filterLabelParts> }).raw;
  return { label, identifier };
};

function filterLabel(filter: TrackItMeasurementFilterForCombinationDto): string {
  const { label, identifier } = filterLabelParts(filter);
  return identifier ? `${label} | ${identifier}` : label;
}

// `value` is the identity key (unique across entries sharing a name); `title` shows the full spec.
const suggestedNameItems = computed(() =>
  parameterEntries.value
    .filter((entry) => !entry.isCustom && trackItFilterName(entry))
    .map((entry) => ({
      title: filterLabel(entry),
      value: trackItFilterIdentityKey(entry),
      ...filterLabelParts(entry),
    })),
);
const suggestedPropertyNames = computed(() =>
  propertyEntries.value.map(trackItFilterName).filter(truthy),
);

// Also lists properties already on the combinations, so one the catalog no longer offers stays
// visible and uncheckable instead of silently staying selected.
const measuringPropertyOptions = computed(() =>
  uniq([
    ...suggestedPropertyNames.value,
    ...props.propertiesFromCombinations.map(trackItFilterName).filter(truthy),
  ]),
);

const workingSelection = ref<string[]>([]);
const additionalRows = ref<AdditionalRow[]>([]);
const workingMeasuringProperties = ref<string[]>([]);

// Cleared on dialog open and on the user's next edit.
const recommendationsNotice = ref<NotificationMessage>();

function clearRecommendationsNotice() {
  recommendationsNotice.value = undefined;
}

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      workingSelection.value = parameterEntries.value
        .filter((entry) => !entry.isCustom && entry.isEnabled === true && trackItFilterName(entry))
        .map(trackItFilterIdentityKey);
      additionalRows.value = parameterEntries.value
        .filter((entry) => entry.isCustom)
        .map(additionalRowFromFilter);
      workingMeasuringProperties.value = props.propertiesFromCombinations
        .map(trackItFilterName)
        .filter(truthy);
      recommendationsNotice.value = undefined;
    }
    reset();
    touchedKeys.value = new Set();
    showAllErrors.value = false;
  },
);

// Re-applies the catalog's recommendation on every catalog change, re-checking a name the user
// just unchecked. The additional rows stay untouched.
watch(
  () => props.trackItFilterCatalog,
  () => {
    if (mode.value === 'details' || !props.modelValue) return;

    const recommendedParameterKeys = parameterEntries.value
      .filter((entry) => entry.isEnabled && !entry.isCustom && trackItFilterName(entry))
      .map(trackItFilterIdentityKey);
    const gotNewParameters = recommendedParameterKeys.some(
      (key) => !workingSelection.value.includes(key),
    );
    workingSelection.value = recommendedParameterKeys;

    const recommendedPropertyNames = propertyEntries.value
      .filter((entry) => entry.isEnabled)
      .map(trackItFilterName)
      .filter(truthy);
    const gotNewProperties = recommendedPropertyNames.some(
      (name) => !workingMeasuringProperties.value.includes(name),
    );
    workingMeasuringProperties.value = recommendedPropertyNames;

    if (gotNewParameters || gotNewProperties) {
      recommendationsNotice.value = {
        type: 'info',
        closable: false,
        message: t('TrackItRecommendationsAddedMessage' as I18nKey),
      };
    }
  },
);

const tInterpolated = t as (key: string, options: Record<string, unknown>) => string;

const keptParameterIdentities = computed(() => {
  const fromAdditionalRows = additionalRows.value.map((row) =>
    trackItParameterIdentityKey(row.name.trim(), row.kind, row.unit?.trim() || undefined),
  );
  return new Set([...workingSelection.value, ...fromAdditionalRows]);
});

// Value-bearing filters no longer staged (Apply would delete their values), keyed by identity so
// undo can re-add exactly these.
const pendingValueLossByIdentity = computed(() => {
  const keptParameters = keptParameterIdentities.value;
  const keptProperties = new Set(workingMeasuringProperties.value.map(trackItPropertyIdentityKey));

  const removedParameters = props.filtersFromCombinations.filter((filter) => {
    const key = trackItFilterIdentityKey(filter);
    return !keptParameters.has(key) && (props.valueBearingCounts.get(key) ?? 0) > 0;
  });
  const removedProperties = props.propertiesFromCombinations.filter((filter) => {
    const key = trackItFilterIdentityKey(filter);
    return !keptProperties.has(key) && (props.valueBearingPropertyCounts.get(key) ?? 0) > 0;
  });

  return new Map(
    [...removedParameters, ...removedProperties].map((filter) => [
      trackItFilterIdentityKey(filter),
      filter,
    ]),
  );
});

const removalWarning = computed<NotificationMessage | undefined>(() =>
  mode.value !== 'details' && pendingValueLossByIdentity.value.size
    ? {
        type: 'warning',
        closable: false,
        message: tInterpolated('TrackItRemovalDeletesValuesWarning', {
          names: [...pendingValueLossByIdentity.value.values()]
            .map((filter) => `"${filterLabel(filter)}"`)
            .join(', '),
        }),
      }
    : undefined,
);

function undoRemovals() {
  clearRecommendationsNotice();
  for (const filter of pendingValueLossByIdentity.value.values()) {
    if (isParameterFilterEntry(filter)) {
      if (filter.isCustom) additionalRows.value.push(additionalRowFromFilter(filter));
      else workingSelection.value.push(trackItFilterIdentityKey(filter));
    } else {
      const name = trackItFilterName(filter);
      if (name) workingMeasuringProperties.value.push(name);
    }
  }
}

function addCustomFilter() {
  clearRecommendationsNotice();
  additionalRows.value.push({ name: '', kind: 'String' });
}

function removeCustomFilter(index: number) {
  clearRecommendationsNotice();
  additionalRows.value.splice(index, 1);
  validateFilters();
}

function close() {
  emit('update:modelValue', false);
}

// Stable per-row keys, so a validation error stays on its row when an earlier row is deleted.
const additionalRowKeys = new WeakMap<AdditionalRow, string>();
let nextAdditionalRowKey = 0;

function additionalRowKey(row: AdditionalRow) {
  const rawRow = toRaw(row);
  let key = additionalRowKeys.get(rawRow);
  if (!key) {
    key = `row:${nextAdditionalRowKey++}`;
    additionalRowKeys.set(rawRow, key);
  }
  return key;
}

const SELECTION_KEY_PREFIX = 'selection:';
const selectionKey = (identityKey: string) => `${SELECTION_KEY_PREFIX}${identityKey}`;
const propertyKey = (name: string) => `property:${name}`;

// Staged catalog picks plus additional rows, for Apply and the suggestion request, so the
// backend sees this session's edits.
const stagedParameterFilters = computed<TrackItMeasurementParameterFilterDto[]>(() => {
  const cleanedRows = additionalRows.value
    .map((row) => ({ ...row, name: row.name.trim() }))
    .filter((row) => row.name);

  return [
    ...workingSelection.value
      .map((key) => parameterEntryByIdentityKey.value.get(key))
      .filter(truthy)
      .map((entry) =>
        createParameterFilterEntry(
          trackItFilterName(entry) ?? '',
          entry.measurementParameter ?? undefined,
          false,
        ),
      ),
    ...cleanedRows.map((row) =>
      createParameterFilterEntry(
        row.name,
        createMeasurementParameter(
          row.kind,
          row.name,
          measurementParameterKindAllowsUnit(row.kind) ? row.unit?.trim() || undefined : undefined,
        ),
        true,
      ),
    ),
  ];
});

const stagedProperties = computed<TrackItMeasurementPropertyFilterDto[]>(() =>
  workingMeasuringProperties.value.map((name) =>
    createPropertyFilterEntry(name, !propertyEntryByName.value.has(name)),
  ),
);

// Unlike Apply, validation sees every filter the dialog offers — unselected ones flagged as
// disabled, and "Additional" rows even while their name is still empty.
const validationEntries = computed<TrackItFilterValidationEntry[]>(() => [
  ...parameterEntries.value
    .filter((entry) => !entry.isCustom && trackItFilterName(entry))
    .map((entry) => {
      const identityKey = trackItFilterIdentityKey(entry);
      return {
        key: selectionKey(identityKey),
        filter: {
          ...createParameterFilterEntry(
            trackItFilterName(entry) ?? '',
            entry.measurementParameter ?? undefined,
            false,
          ),
          isEnabled: workingSelection.value.includes(identityKey),
        },
      };
    }),
  ...additionalRows.value.map((row) => {
    const name = row.name.trim();
    return {
      key: additionalRowKey(row),
      filter: createParameterFilterEntry(
        name,
        createMeasurementParameter(
          row.kind,
          name,
          measurementParameterKindAllowsUnit(row.kind) ? row.unit?.trim() || undefined : undefined,
        ),
        true,
      ),
    };
  }),
  ...measuringPropertyOptions.value.map((name) => ({
    key: propertyKey(name),
    filter: {
      ...createPropertyFilterEntry(name, !propertyEntryByName.value.has(name)),
      isEnabled: workingMeasuringProperties.value.includes(name),
    },
  })),
]);

const { errorsByKey, validating, validate, reset } = useTrackItFilterValidation(validationEntries);

function validateFilters() {
  if (mode.value !== 'details') validate();
}

// Picking or dropping a catalog filter or a measuring property is a completed edit, unlike
// typing a name.
watch(
  [workingSelection, workingMeasuringProperties],
  () => {
    if (props.modelValue) validateFilters();
  },
  { deep: true },
);

// An error shows only once its element was left (or toggled), or after an Apply attempt —
// a freshly added row must not open with a "required" message.
const touchedKeys = ref(new Set<string>());
const showAllErrors = ref(false);

function touch(key: string) {
  touchedKeys.value = new Set([...touchedKeys.value, key]);
}

// Catalog picks are never typed in, so their errors show right away.
function visibleError(key: string) {
  return showAllErrors.value || touchedKeys.value.has(key) || key.startsWith(SELECTION_KEY_PREFIX)
    ? errorsByKey.value.get(key)
    : undefined;
}

function selectionItemError(value: unknown) {
  return typeof value === 'string' ? visibleError(selectionKey(value)) : undefined;
}

// The errors themselves are shown on the items and chips; the field only turns red.
const hasSelectionErrors = computed(() =>
  workingSelection.value.some((identityKey) => visibleError(selectionKey(identityKey))),
);

const hasVisibleErrors = computed(() =>
  [...errorsByKey.value.keys()].some((key) => visibleError(key)),
);

// Stays clickable while errors are still hidden: clicking reveals them instead of applying.
const canApply = computed(() => !validating.value && !hasVisibleErrors.value);

async function apply() {
  if (validating.value) return;

  if (!(await validate())) {
    showAllErrors.value = true;
    return;
  }

  emit('apply', stagedParameterFilters.value, stagedProperties.value);
  close();
}
</script>

<template>
  <Dialog :modelValue="modelValue" size="large" @update:modelValue="close">
    <template #title>
      <span>{{ $t('SelectTrackItFilters' as I18nKey) }}</span>
    </template>

    <template #text>
      <p class="text-primary-darken-2 mb-6">
        {{ $t('TrackItFiltersDescription' as I18nKey) }}
      </p>

      <Notification v-if="recommendationsNotice" :value="recommendationsNotice" class="mb-4" />

      <Notification v-if="removalWarning" :value="removalWarning" class="mb-4">
        <template #append>
          <Button
            variant="ghost"
            class="undo-removal"
            :icon="mdiUndo"
            iconPosition="start"
            :label="$t('UndoRemoval' as I18nKey)"
            @click="undoRemovals"
          />
        </template>
      </Notification>

      <h6 class="text-body-2 font-weight-bold text-primary-darken-2">
        {{ $t('TrackItMeasurementParameter_other' as I18nKey) }}
      </h6>

      <div class="d-flex align-start ga-2 mb-4">
        <Autocomplete
          v-model="workingSelection"
          class="flex-grow-1"
          :items="suggestedNameItems"
          multiple
          chips
          closableChips
          clearable
          :error="hasSelectionErrors"
          @update:modelValue="clearRecommendationsNotice"
        >
          <template #itemTitle="itemSlot">
            <IdentifierLabel
              v-bind="itemParts(itemSlot)"
              :class="{ 'text-error': selectionItemError(itemSlot.value) }"
            />
            <div v-if="selectionItemError(itemSlot.value)" class="text-caption text-error">
              {{ selectionItemError(itemSlot.value) }}
            </div>
          </template>
          <template #chip="{ props: chipProps, item }">
            <v-chip
              closable
              class="text-body-2"
              v-bind="chipProps"
              :color="selectionItemError(item.value) ? 'error' : undefined"
            >
              <IdentifierLabel v-bind="itemParts(item)" />
            </v-chip>
          </template>
        </Autocomplete>

        <Button
          v-if="mode !== 'details'"
          variant="secondary"
          class="flex-shrink-0"
          :label="$t('SuggestTrackItParameters' as I18nKey)"
          @click="emit('requestParameterSuggestions', stagedParameterFilters)"
        />
      </div>

      <p class="text-body-2 text-primary-darken-2 mb-2">
        {{ $t('TrackItAddMeasurementParametersHint' as I18nKey) }}
      </p>

      <v-list class="bg-transparent pa-0" density="compact">
        <v-list-item v-for="(row, index) in additionalRows" :key="index" class="pl-0">
          <div class="d-flex align-start ga-2">
            <TextField
              v-model="row.name"
              emptyValue=""
              :label="$t('Name')"
              density="compact"
              :hideDetails="!visibleError(additionalRowKey(row))"
              :errorMessage="visibleError(additionalRowKey(row))"
              class="flex-grow-1"
              @update:modelValue="clearRecommendationsNotice"
              @blur="
                touch(additionalRowKey(row));
                validateFilters();
              "
            />
            <Select
              v-model="row.kind"
              :items="trackItMeasurementParameterKinds"
              :label="$t('ValueType')"
              density="compact"
              hideDetails
              class="unit-row-select"
              @update:modelValue="clearRecommendationsNotice"
            />
            <!-- Always shown; disabled and empty for a kind without a unit (Boolean/Modality/
                 String). row.unit is kept, so switching back to a unit-bearing kind restores it. -->
            <TrackItUnitCombobox
              :modelValue="measurementParameterKindAllowsUnit(row.kind) ? row.unit : undefined"
              :label="$t('Unit_one')"
              :disabled="!measurementParameterKindAllowsUnit(row.kind)"
              class="unit-row-select"
              @update:modelValue="
                row.unit = $event ?? undefined;
                clearRecommendationsNotice();
              "
            />
            <!-- mt-6 = label height + gap, so the 44px button lines up with the fields, not the label. -->
            <PtwDeleteButton
              size="default"
              class="mt-6 flex-shrink-0"
              @delete="removeCustomFilter(index)"
            />
          </div>
        </v-list-item>
      </v-list>
      <FormAddButton
        variant="ghost"
        :label="'AddMeasurementParameters' as I18nKey"
        @click="addCustomFilter"
      />

      <v-divider class="my-4" />
      <h6 class="text-body-2 font-weight-bold text-primary-darken-2">
        {{ $t('TrackItMeasuringProperty_other' as I18nKey) }}
      </h6>
      <!-- No "Suggest" here: the backend has no measuring-property values to suggest yet. -->
      <div class="d-flex flex-column">
        <Checkbox
          v-for="name in measuringPropertyOptions"
          :key="name"
          v-model="workingMeasuringProperties"
          :value="name"
          :label="translateMeasurementPropertyName(name)"
          :hideDetails="!visibleError(propertyKey(name))"
          :errorMessage="visibleError(propertyKey(name))"
          @update:modelValue="
            touch(propertyKey(name));
            clearRecommendationsNotice();
          "
        />
      </div>
    </template>

    <template #actions>
      <Button variant="text" :label="$t('Cancel')" @click="close()" />
      <Button
        variant="primary"
        :label="$t('Apply')"
        :disabled="!canApply"
        :loading="validating"
        @click="apply()"
      />
    </template>
  </Dialog>
</template>

<style scoped lang="scss">
// Overrides Button's fixed 44px/bold text variant, which clashes with the inline 14px alert text.
.undo-removal {
  display: inline-flex !important;
  height: auto !important;
  min-height: 0 !important;
  padding: 0 10px !important;
  margin-left: 12px;
  font: inherit !important;
  font-weight: 600 !important;
  letter-spacing: normal !important;

  :deep(.v-icon) {
    font-size: 16px !important;
  }
}

.unit-row-select {
  width: 10em;
  // Raw v-combobox/v-select default to flex: 1 1 auto and would stretch the full dialog width.
  flex: 0 0 auto;
}
</style>
